import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { captureError } from './monitoring';
import { shuffleInPlace } from '../utils/helpers';
import { DEFAULT_PASS_THRESHOLD } from '../utils/constants';

import type {
  JourneyDoc,
  JourneyGroup,
  JourneyStep,
  JourneyConfig,
  StepIdMigration,
  CreateJourneyData,
  UpdateJourneyData,
  UpdateJourneyOptions,
  UpdateJourneyResult,
  PairMap,
  CardGroupRank,
  JourneyCacheEntry,
  DisplayMode,
  MergeMode,
  ReviewDisplayModes,
  ReviewModes,
  ReviewStepSizes,
  StepType,
} from '../types/journey';

export type {
  JourneyDoc,
  JourneyGroup,
  JourneyStep,
  JourneyConfig,
  StepIdMigration,
  CreateJourneyData,
  UpdateJourneyData,
  UpdateJourneyOptions,
  UpdateJourneyResult,
  PairMap,
  CardGroupRank,
  JourneyCacheEntry,
  DisplayMode,
  MergeMode,
  ReviewDisplayModes,
  ReviewModes,
  ReviewStepSizes,
  StepType,
};

const COLLECTION = 'journeys';

export const SMART_MERGE_SIZE = 20;
const STALE_DAYS = 7;

const _journeyCache = new Map<string, JourneyCacheEntry>();
const JOURNEY_CACHE_TTL = 30_000;
const JOURNEY_CACHE_MAX = 50;

export function invalidateJourneyCache(id?: string): void {
  if (id) _journeyCache.delete(id);
  else _journeyCache.clear();
}

function generateId(): string {
  return crypto.randomUUID().replace(/-/g, '').substring(0, 8);
}

function toMillis(t: unknown): number {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t === 'object' && t !== null && 'toMillis' in t && typeof (t as { toMillis: () => number }).toMillis === 'function') {
    return (t as { toMillis: () => number }).toMillis();
  }
  if (typeof t === 'object' && t !== null && 'seconds' in t) {
    return (t as { seconds: number }).seconds * 1000;
  }
  return 0;
}

/**
 * Builds a limited-size session from a merge step's card pool,
 * distributing slots proportionally by station recency.
 *
 * Weight per station = (stationIndex + 1), so the newest station always
 * receives more slots than older ones.
 * Example — 3 stations, 20 slots: station3→10, station2→7, station1→3.
 *
 * Cards within each station are shuffled for variety.
 * Falls back to a plain shuffle when no rank data is supplied.
 */
export function buildSmartMergeSession(
  allCardIds: string[],
  maxSize: number = SMART_MERGE_SIZE,
  pairMap: PairMap = {},
  cardGroupRank: CardGroupRank = {},
): string[] {
  let unique = [...new Set(allCardIds)];

  const usedPairs = new Set<string>();
  unique = unique.filter(id => {
    const pid = pairMap[id];
    if (pid && usedPairs.has(pid)) return false;
    if (pid) usedPairs.add(pid);
    return true;
  });

  if (unique.length <= maxSize) return shuffleInPlace([...unique]);

  if (Object.keys(cardGroupRank).length === 0) {
    return shuffleInPlace([...unique]).slice(0, maxSize);
  }

  const byRank = new Map<number, string[]>();
  for (const id of unique) {
    const r = cardGroupRank[id] ?? 0;
    if (!byRank.has(r)) byRank.set(r, []);
    byRank.get(r)!.push(id);
  }
  for (const arr of byRank.values()) shuffleInPlace(arr);

  const ranks = [...byRank.keys()].sort((a, b) => b - a);
  const totalWeight = ranks.reduce((s, r) => s + r + 1, 0);

  const selected: string[] = [];
  let slotsLeft = maxSize;

  for (let i = 0; i < ranks.length; i++) {
    const r = ranks[i];
    const arr = byRank.get(r)!;
    const isLast = i === ranks.length - 1;
    const quota = isLast ? slotsLeft : Math.floor((r + 1) / totalWeight * maxSize);
    const take = Math.min(quota, arr.length, slotsLeft);
    selected.push(...arr.splice(0, take));
    slotsLeft -= take;
    if (slotsLeft <= 0) break;
  }

  if (selected.length < maxSize) {
    const leftover = [...byRank.values()].flat();
    shuffleInPlace(leftover);
    selected.push(...leftover.slice(0, maxSize - selected.length));
  }

  return shuffleInPlace(selected);
}

// ─── Review Display Modes ────────────────────────────

export function getReviewDisplayModes(
  config: Pick<JourneyConfig, 'reviewDisplayModes' | 'reviewModes' | 'mergeMode'>,
  reviewKey: string,
): DisplayMode[] {
  const { reviewDisplayModes = {}, reviewModes = {}, mergeMode = 'mixed' } = config;
  if (reviewDisplayModes[reviewKey]?.length > 0) return reviewDisplayModes[reviewKey];
  const mode = reviewModes[reviewKey] || mergeMode;
  if (mode === 'separated') return ['flashcards', 'drills'];
  return ['mixed'];
}

// ─── Display Modes ──────────────────────────────────

export function getGroupDisplayModes(g: JourneyGroup): DisplayMode[] {
  if (Array.isArray(g.displayModes)) return g.displayModes;
  const hasContent = (g.cardIds?.length ?? 0) > 0 || (g.items?.length ?? 0) > 0
    || (g.wordCardIds?.length ?? 0) > 0 || (g.flashcardIds?.length ?? 0) > 0
    || (g.questionCardIds?.length ?? 0) > 0 || (g.words?.length ?? 0) > 0
    || (g.cards?.length ?? 0) > 0 || (g.questions?.length ?? 0) > 0;
  if (!hasContent) return [];
  const disabled = g.disabledStepTypes || [];
  const modes: DisplayMode[] = [];
  if (!disabled.includes('read')) modes.push('read');
  if (!disabled.includes('flashcards')) modes.push('flashcards');
  if (!disabled.includes('drills')) modes.push('drills');
  return modes.length > 0 ? modes : ['read', 'flashcards', 'drills'];
}

export function isReviewEnabled(g: JourneyGroup, groupIndex: number): boolean {
  if (g.reviewEnabled !== undefined) return g.reviewEnabled;
  return groupIndex > 0;
}

// ─── Per-group step checks (respect displayModes) ───

function groupHasWords(g: JourneyGroup): boolean {
  if (!getGroupDisplayModes(g).includes('read')) return false;
  if ((g.cardIds?.length ?? 0) > 0) return true;
  if ((g.wordCardIds?.length ?? 0) > 0) return true;
  return (g.words?.length ?? 0) > 0;
}
function groupHasCards(g: JourneyGroup): boolean {
  if (!getGroupDisplayModes(g).includes('flashcards')) return false;
  if ((g.cardIds?.length ?? 0) > 0) return true;
  if ((g.flashcardIds?.length ?? 0) > 0) return true;
  return (g.cards?.length ?? 0) > 0;
}
function groupHasQuestions(g: JourneyGroup): boolean {
  if (!getGroupDisplayModes(g).includes('drills')) return false;
  if ((g.cardIds?.length ?? 0) > 0) return g.hasDrills !== false;
  if ((g.questionCardIds?.length ?? 0) > 0) return true;
  return (g.questions?.length ?? 0) > 0;
}
function groupHasMatching(g: JourneyGroup): boolean {
  if (!getGroupDisplayModes(g).includes('matching')) return false;
  if ((g.cardIds?.length ?? 0) > 0) return true;
  if ((g.flashcardIds?.length ?? 0) > 0 || (g.questionCardIds?.length ?? 0) > 0) return true;
  return ((g.cards?.length ?? 0) > 0) || ((g.questions?.length ?? 0) > 0);
}
function groupHasMixed(g: JourneyGroup): boolean {
  if (!getGroupDisplayModes(g).includes('mixed')) return false;
  if ((g.cardIds?.length ?? 0) > 0) return true;
  if ((g.flashcardIds?.length ?? 0) > 0 || (g.questionCardIds?.length ?? 0) > 0) return true;
  return ((g.cards?.length ?? 0) > 0) || ((g.questions?.length ?? 0) > 0);
}

// ─── Merge checks (ignore displayModes — check raw content) ───

function groupHasCardContent(g: JourneyGroup): boolean {
  if ((g.cardIds?.length ?? 0) > 0) return true;
  if ((g.flashcardIds?.length ?? 0) > 0) return true;
  return (g.cards?.length ?? 0) > 0;
}
function groupHasDrillContent(g: JourneyGroup): boolean {
  if ((g.cardIds?.length ?? 0) > 0) return g.hasDrills !== false;
  if ((g.questionCardIds?.length ?? 0) > 0) return true;
  return (g.questions?.length ?? 0) > 0;
}

function anyGroupHasCardContent(groups: JourneyGroup[], ids: string[]): boolean {
  return ids.some(id => { const g = groups.find(gr => gr.id === id); return g != null && groupHasCardContent(g); });
}
function anyGroupHasDrillContent(groups: JourneyGroup[], ids: string[]): boolean {
  return ids.some(id => { const g = groups.find(gr => gr.id === id); return g != null && groupHasDrillContent(g); });
}
function anyGroupHasAnyContent(groups: JourneyGroup[], ids: string[]): boolean {
  return ids.some(id => { const g = groups.find(gr => gr.id === id); return g != null && (groupHasCardContent(g) || groupHasDrillContent(g)); });
}

// ─── Step generation ────────────────────────────────

function arraysEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false;
  const sa = [...a].sort(), sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

export const REVIEW_STEP_DEFAULT = 10;
export const REVIEW_STEP_MIN = 3;

export function generateSteps(
  groups: JourneyGroup[],
  {
    mergeMode = 'mixed',
    reviewModes = {},
    reviewDisplayModes = {},
    reviewStepSizes = {},
    reviewSizes = {},
    oldSteps = [],
  }: JourneyConfig = {},
): JourneyStep[] {
  if (!groups || groups.length === 0) return [];

  const steps: JourneyStep[] = [];
  const stableId = (type: string, groupIds: string[], isMerge?: boolean): string => {
    const gids = groupIds.join('_');
    return isMerge ? `${type}_merge_${gids}` : `${type}_${gids}`;
  };

  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const groupNum = i + 1;

    if (groupHasWords(g)) {
      steps.push({ id: stableId('read', [g.id]), type: 'read', groupIds: [g.id], title: `قراءة المحطة ${groupNum}` });
    }

    if (groupHasMatching(g)) {
      const matchStep: JourneyStep = { id: stableId('matching', [g.id]), type: 'matching', groupIds: [g.id], title: `توصيل المحطة ${groupNum}` };
      if (g.matchingPairCount) matchStep.matchingPairCount = g.matchingPairCount;
      steps.push(matchStep);
    }

    if (groupHasCards(g)) {
      steps.push({ id: stableId('flashcards', [g.id]), type: 'flashcards', groupIds: [g.id], title: `بطاقات المحطة ${groupNum}` });
    }
    if (groupHasQuestions(g)) {
      steps.push({ id: stableId('drills', [g.id]), type: 'drills', groupIds: [g.id], title: `تدريبات المحطة ${groupNum}` });
    }
    if (groupHasMixed(g)) {
      steps.push({ id: stableId('mixed', [g.id]), type: 'mixed', groupIds: [g.id], title: `بطاقات وتدريبات المحطة ${groupNum}` });
    }

    const hasReview = isReviewEnabled(g, i);

    if (!hasReview) {
      steps.push({ id: stableId('checkpoint', [g.id]), type: 'checkpoint', groupIds: [g.id], title: `إتمام المحطة ${groupNum}` });
    }
    if (hasReview) {
      const allPreviousIds = groups.slice(0, i + 1).map((pg) => pg.id);
      const isFinal = allPreviousIds.length === groups.length;
      const rangeLabel = isFinal
        ? 'جلسة تثبيت'
        : `تثبيت ${groups.slice(0, i + 1).map((_, j) => j + 1).join('+')}`;
      const mergeNumLabel = groups.slice(0, i + 1).map((_, j) => j + 1).join('+');

      const reviewKey = allPreviousIds.join('_');
      const rawModes = getReviewDisplayModes({ reviewDisplayModes, reviewModes, mergeMode }, reviewKey);
      const REVIEW_ORDER: DisplayMode[] = ['matching', 'flashcards', 'drills', 'mixed'];
      const modes = [...rawModes].sort((a, b) => REVIEW_ORDER.indexOf(a) - REVIEW_ORDER.indexOf(b));

      const REVIEW_TITLES: Record<string, [() => string, () => string]> = {
        matching:   [() => `توصيل مراجعة ${mergeNumLabel}`,  () => 'توصيل مراجعة نهائية'],
        flashcards: [() => `بطاقات مراجعة ${mergeNumLabel}`, () => 'بطاقات مراجعة نهائية'],
        drills:     [() => `تدريبات مراجعة ${mergeNumLabel}`,() => 'تدريبات مراجعة نهائية'],
        mixed:      [() => `مراجعة ${mergeNumLabel}`,        () => 'مراجعة نهائية'],
      };

      for (const rMode of modes) {
        const titleFns = REVIEW_TITLES[rMode];
        if (!titleFns) continue;
        const title = isFinal ? titleFns[1]() : titleFns[0]();
        const stepSizeKey = `${reviewKey}_${rMode}`;
        const stepSize = reviewStepSizes[stepSizeKey] || REVIEW_STEP_DEFAULT;
        const base: Partial<JourneyStep> = { groupIds: allPreviousIds, isMerge: true, reviewKey, mergeSize: stepSize };

        if (rMode === 'matching') {
          steps.push({ id: stableId('matching', allPreviousIds, true), type: 'matching', ...base, title, matchingPairCount: stepSize } as JourneyStep);
        } else if (rMode === 'flashcards' && anyGroupHasCardContent(groups, allPreviousIds)) {
          steps.push({ id: stableId('flashcards', allPreviousIds, true), type: 'flashcards', ...base, title } as JourneyStep);
        } else if (rMode === 'drills' && anyGroupHasDrillContent(groups, allPreviousIds)) {
          steps.push({ id: stableId('drills', allPreviousIds, true), type: 'drills', ...base, title } as JourneyStep);
        } else if (rMode === 'mixed' && anyGroupHasAnyContent(groups, allPreviousIds)) {
          steps.push({ id: stableId('mixed', allPreviousIds, true), type: 'mixed', ...base, title } as JourneyStep);
        }
      }

      const checkpointTitle = isFinal ? 'إتمام الرحلة' : `إتمام المحطة ${groupNum}`;
      steps.push({ id: stableId('checkpoint', allPreviousIds), type: 'checkpoint', groupIds: allPreviousIds, title: checkpointTitle });
    }
  }

  for (const old of oldSteps) {
    if (!old.customTitle) continue;
    const match = steps.find(s =>
      s.type === old.type && arraysEqual(s.groupIds, old.groupIds) && !!s.isMerge === !!old.isMerge
    );
    if (match) {
      match.title = old.title;
      match.customTitle = true;
    }
  }

  return steps;
}

export function createEmptyGroup(index: number): JourneyGroup {
  return {
    id: generateId(),
    name: `المحطة ${index + 1}`,
    deckId: null,
    cardIds: [],
    displayModes: ['read', 'mixed'],
    flipHint: '',
  };
}

/**
 * Migrate a group from the old 3-array format to the unified cardIds format.
 * Safe to call on already-migrated groups.
 */
export function migrateGroup(group: JourneyGroup): JourneyGroup {
  if (group.cardIds) return group;
  const idSet = new Set([
    ...(group.wordCardIds || []),
    ...(group.flashcardIds || []),
    ...(group.questionCardIds || []),
  ]);
  return {
    ...group,
    cardIds: [...idSet],
    hasDrills: (group.questionCardIds || []).length > 0,
  };
}

export async function createJourney(data: CreateJourneyData): Promise<string> {
  const mergeMode: MergeMode = data.mergeMode || 'mixed';
  const steps = generateSteps(data.groups || [], { mergeMode });
  const classId = typeof data.classId === 'string' ? data.classId : null;
  const docRef = await addDoc(collection(db, COLLECTION), {
    title: data.title || '',
    description: data.description || '',
    classId,
    ownerId: data.ownerId,
    active: true,
    standalone: !!data.standalone,
    sortOrder: data.sortOrder ?? 0,
    passThreshold: data.passThreshold ?? DEFAULT_PASS_THRESHOLD,
    mergeMode,
    groups: data.groups || [],
    steps,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function getJourney(id: string): Promise<JourneyDoc | null> {
  try {
    const cached = _journeyCache.get(id);
    if (cached && Date.now() - cached.ts < JOURNEY_CACHE_TTL) return cached.data;

    const snap = await getDoc(doc(db, COLLECTION, id));
    if (!snap.exists()) return null;
    // Firestore data shape is not guaranteed — legacy docs may differ
    const data = { id: snap.id, ...snap.data() } as JourneyDoc;
    if (_journeyCache.size >= JOURNEY_CACHE_MAX) {
      const oldest = _journeyCache.keys().next().value;
      if (oldest) _journeyCache.delete(oldest);
    }
    _journeyCache.set(id, { data, ts: Date.now() });
    return data;
  } catch (err: any) {
    if (err?.code !== 'permission-denied' && err?.code !== 'not-found') {
      captureError(err, { operation: 'getJourney', journeyId: id });
    }
    throw err;
  }
}

export async function getJourneysByClass(classId: string): Promise<JourneyDoc[]> {
  const q = query(
    collection(db, COLLECTION),
    where('classId', '==', classId)
  );
  const snap = await getDocs(q);
  // Firestore data may contain legacy fields
  const results = snap.docs.map((d) => ({ id: d.id, ...d.data() } as JourneyDoc));
  return results.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
}

export async function getJourneysByOwner(ownerId: string): Promise<JourneyDoc[]> {
  const q = query(
    collection(db, COLLECTION),
    where('ownerId', '==', ownerId)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => ({ id: d.id, ...d.data() } as JourneyDoc));
  return results.sort((a, b) => {
    const ta = toMillis(a.createdAt);
    const tb = toMillis(b.createdAt);
    return tb - ta;
  });
}

export async function getAllJourneys(): Promise<JourneyDoc[]> {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as JourneyDoc));
}

export async function updateJourney(
  id: string,
  data: UpdateJourneyData,
  { oldSteps: passedOldSteps, existingMigration: passedMigration }: UpdateJourneyOptions = {},
): Promise<UpdateJourneyResult> {
  if ('classId' in data && typeof data.classId !== 'string' && data.classId !== null && data.classId !== undefined) {
    data.classId = null;
  }
  const update: Record<string, unknown> = { ...data, updatedAt: serverTimestamp() };
  let result: UpdateJourneyResult = {};

  if (data.groups) {
    let oldSteps = passedOldSteps;
    let existingMigration = passedMigration;

    if (!oldSteps) {
      const currentSnap = await getDoc(doc(db, COLLECTION, id));
      const currentData = currentSnap.exists() ? (currentSnap.data() as Partial<JourneyDoc>) : {};
      oldSteps = (currentData.steps || []) as JourneyStep[];
      existingMigration = (currentData.stepIdMigration || {}) as StepIdMigration;
      if (!data.mergeMode) data.mergeMode = currentData.mergeMode;
      if (!data.reviewModes) data.reviewModes = currentData.reviewModes;
      if (!data.reviewDisplayModes) data.reviewDisplayModes = currentData.reviewDisplayModes;
      if (!data.reviewStepSizes) data.reviewStepSizes = currentData.reviewStepSizes;
    }
    existingMigration = existingMigration || {};

    const newSteps = generateSteps(data.groups, {
      mergeMode: data.mergeMode || 'mixed',
      reviewModes: data.reviewModes || {},
      reviewDisplayModes: data.reviewDisplayModes || {},
      reviewStepSizes: data.reviewStepSizes || {},
      oldSteps,
    });
    update.steps = newSteps;

    const newMigration: StepIdMigration = {};
    for (const oldStep of oldSteps!) {
      const match = newSteps.find(s =>
        s.type === oldStep.type &&
        arraysEqual(s.groupIds, oldStep.groupIds) &&
        !!s.isMerge === !!oldStep.isMerge
      );
      if (match && match.id !== oldStep.id) {
        newMigration[oldStep.id] = match.id;
      }
    }

    const merged: StepIdMigration = {};
    for (const [origId, intermediateId] of Object.entries(existingMigration)) {
      merged[origId] = newMigration[intermediateId] || intermediateId;
    }
    for (const [oldId, newId] of Object.entries(newMigration)) {
      if (!merged[oldId]) merged[oldId] = newId;
    }

    const validStepIds = new Set(newSteps.map(s => s.id));
    for (const origId of Object.keys(merged)) {
      if (!validStepIds.has(merged[origId])) delete merged[origId];
    }
    update.stepIdMigration = Object.keys(merged).length > 0 ? merged : null;

    let newBadgeSteps = (update.newBadgeSteps as string[] | undefined) || [];
    if (newBadgeSteps.length > 0) {
      if (Object.keys(newMigration).length > 0) {
        newBadgeSteps = newBadgeSteps.map(sid => newMigration[sid] || sid);
      }
      newBadgeSteps = newBadgeSteps.filter(sid => newSteps.some(s => s.id === sid));
      update.newBadgeSteps = newBadgeSteps;
    }

    const oldCheckpointIds = new Set(
      oldSteps!.filter(s => s.type === 'checkpoint').map(s => newMigration[s.id] || s.id)
    );
    const autoNewBadge: string[] = [];
    let stationBuf: JourneyStep[] = [];
    for (const step of newSteps) {
      stationBuf.push(step);
      if (step.type === 'checkpoint') {
        if (!oldCheckpointIds.has(step.id)) {
          for (const s of stationBuf) {
            if (s.type !== 'checkpoint') autoNewBadge.push(s.id);
          }
        }
        stationBuf = [];
      }
    }
    if (autoNewBadge.length > 0) {
      const existing = (update.newBadgeSteps as string[]) || [];
      update.newBadgeSteps = [...new Set([...existing, ...autoNewBadge])];
    }

    result = {
      steps: newSteps,
      stepIdMigration: update.stepIdMigration as StepIdMigration | null | undefined,
      newBadgeSteps: update.newBadgeSteps as string[] | undefined,
    };
  }

  await updateDoc(doc(db, COLLECTION, id), update);
  _journeyCache.delete(id);
  return result;
}

export async function swapJourneySortOrder(
  id1: string, sortOrder1: number,
  id2: string, sortOrder2: number,
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION, id1), { sortOrder: sortOrder1, updatedAt: serverTimestamp() });
  batch.update(doc(db, COLLECTION, id2), { sortOrder: sortOrder2, updatedAt: serverTimestamp() });
  await batch.commit();
  _journeyCache.delete(id1);
  _journeyCache.delete(id2);
}

export async function trashJourney(id: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), { trashedAt: serverTimestamp() });
  _journeyCache.delete(id);
}

export async function restoreJourney(id: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), { trashedAt: null });
  _journeyCache.delete(id);
}

export async function deleteJourney(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
  _journeyCache.delete(id);
}

export async function uploadJourneyImage(journeyId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop();
  const storageRef = ref(storage, `journeys/${journeyId}/cover.${ext}`);
  const snap = await uploadBytes(storageRef, file);
  return getDownloadURL(snap.ref);
}

export async function deleteJourneyImage(url: string): Promise<void> {
  try {
    const storageRef = ref(storage, url);
    await deleteObject(storageRef);
  } catch {
    // already deleted or invalid
  }
}
